import { useCallback, useEffect, useRef } from 'react';

import { UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useTheme from 'api/useTheme';
import { AxiosError } from 'axios';
import { systemTexts } from 'constants/localization';
import { EToasterStatus, showToast } from 'utils/showToast';

import { IStoreSettingsFormValues } from '../../../StoreSettings.types';
import {
  QuantityFieldPath,
  QuantityFieldValue
} from '../AbbreviationsSettingsV2.types';

const AUTOSAVE_DEBOUNCE_MILLISECONDS = 600;

interface IUseAbbreviationsAutosaveProps {
  form: UseFormReturn<IStoreSettingsFormValues>;
}

interface IUseAbbreviationsAutosaveReturn {
  commitField: (path: QuantityFieldPath, value: QuantityFieldValue) => void;
  isSaving: boolean;
}

/**
 * Wires Store Settings abbreviation fields to an auto-saving React Hook Form.
 * Each committed change updates the form and schedules a single debounced
 * `updateGeneralTheme` mutation with the latest, validated form values.
 *
 * @example
 * const form = useForm<IStoreSettingsFormValues>({ values });
 * const { commitField, isSaving } = useAbbreviationsAutosave({ form });
 * commitField('productsQuantityFormat.thousandShorthand', true);
 */
export const useAbbreviationsAutosave = ({
  form
}: IUseAbbreviationsAutosaveProps): IUseAbbreviationsAutosaveReturn => {
  const { publisherId } = useParams();
  const { updateGeneralTheme } = useTheme(publisherId);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const persistLatestValues = useCallback(async (): Promise<void> => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }
    try {
      await updateGeneralTheme.mutateAsync(form.getValues());
    } catch (error) {
      showToast({
        message: systemTexts.settingsTexts.THEME_UPDATE_ERROR,
        status: EToasterStatus.ERROR,
        description:
          ((error as AxiosError).response?.data as { message: string })
            ?.message || 'Unknown error occurred'
      });
    }
  }, [form, updateGeneralTheme]);

  const commitField = useCallback(
    (path: QuantityFieldPath, value: QuantityFieldValue): void => {
      form.setValue(path, value, {
        shouldDirty: true,
        shouldValidate: true
      });
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(
        persistLatestValues,
        AUTOSAVE_DEBOUNCE_MILLISECONDS
      );
    },
    [form, persistLatestValues]
  );

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  return { commitField, isSaving: updateGeneralTheme.isLoading };
};
